import React from 'react';
import { Tooltip, Typography, Badge } from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CarOutlined,
    TruckOutlined,
    TruckFilled,
    CaretRightOutlined,
    BgColorsOutlined
} from '@ant-design/icons';
import { DesignIcon, FulfillmentIcon, ShipmentIcon, TrackingIcon, MessageIcon } from '../../../utils/icons';

const { Text } = Typography;

const StatusOverview = ({ record, orderStatus, onMessageClick, allOrderItems = [] }) => {
    // Helper function to count unprocessed messages
    const getUnprocessedMessagesCount = () => {
        if (!record.order?.messages || !Array.isArray(record.order.messages)) return 0;
        
        return record.order.messages.filter(msg => 
            msg.messageType === 'customer_message' && !msg.isProcessed
        ).length;
    };

    // Helper function to check if there are any messages
    const hasAnyMessages = () => {
        if (!record.order?.messages || !Array.isArray(record.order.messages)) return false;
        
        return record.order.messages.some(msg => msg.messageType === 'customer_message');
    };

    // Helper function to get all order items of the same order
    const getOrderItems = () => {
        if (!record.order?.id || !allOrderItems.length) return [record];
        
        return allOrderItems.filter(item => item.order?.id === record.order.id);
    };

    // Helper function to check if status is consistent across all order items
    const getStatusConsistency = (type) => {
        const orderItems = getOrderItems();
        if (orderItems.length <= 1) return { isConsistent: true, statusCounts: {} };

        const statusCounts = {};
        let totalItems = 0;

        orderItems.forEach(item => {
            let statusValue;
            switch (type) {
                case 'customization':
                    statusValue = item.customizationStatus || 'Not Set';
                    break;
                case 'fulfillment':
                    statusValue = item.fulfillmentStatus || 'Not Set';
                    break;
                case 'shipment':
                    statusValue = item.isConfirmShipped ? 'Confirmed' : 'Pending';
                    break;
                case 'tracking':
                    statusValue = item.trackingInfo?.lastStatus || 'No Data';
                    break;
                default:
                    statusValue = 'Unknown';
            }

            statusCounts[statusValue] = (statusCounts[statusValue] || 0) + 1;
            totalItems++;
        });

        const uniqueStatuses = Object.keys(statusCounts);
        const isConsistent = uniqueStatuses.length === 1;

        return { isConsistent, statusCounts, totalItems };
    };

    const unprocessedCount = getUnprocessedMessagesCount();
    const hasMessages = hasAnyMessages();
    // Helper function to get status color class
    const getStatusColorClass = (type, value) => {
        // Check status consistency first
        const consistency = getStatusConsistency(type);
        if (!consistency.isConsistent) {
            return 'status-box-inconsistent';
        }

        switch (type) {
            case 'customization':
                switch (value) {
                    case 'NoCustomization':
                        return 'status-box-gray';
                    case 'TaskCreated':
                    case 'Doing':
                        return 'status-box-blue';
                    case 'InternalReview':
                    case 'Rework':
                        return 'status-box-yellow';
                    case 'MockupReady':
                    case 'MockupSent':
                    case 'Confirming':
                        return 'status-box-purple';
                    case 'CustomerConfirmed':
                    case 'ConfirmedNoReply':
                    case 'AutoFulfill':
                        return 'status-box-green';
                    default:
                        return 'status-box-gray';
                }
            case 'fulfillment':
                switch (value) {
                    case 'Unfulfilled':
                        return 'status-box-gray';
                    case 'Fulfilled':
                        return 'status-box-blue';
                    case 'Shipped':
                        return 'status-box-green';
                    case 'Canceled':
                        return 'status-box-red';
                    default:
                        return 'status-box-gray';
                }
            case 'shipment':
                return value ? 'status-box-green' : 'status-box-gray';
            case 'tracking':
                if (!value || !value.lastStatus) return 'status-box-gray';
                switch (value.lastStatus?.toLowerCase()) {
                    case 'delivered':
                        return 'status-box-green';
                    case 'out_for_delivery':
                        return 'status-box-yellow';
                    case 'in_transit':
                        return 'status-box-blue';
                    case 'pending':
                        return 'status-box-gray';
                    case 'exception':
                    case 'failed':
                        return 'status-box-red';
                    default:
                        return 'status-box-gray';
                }
            default:
                return 'status-box-gray';
        }
    };

    // Helper function to get status icon
    const getStatusIcon = (type, value) => {
        // Check status consistency first
        const consistency = getStatusConsistency(type);
        if (!consistency.isConsistent) {
            // Return gray icon for inconsistent status
            switch (type) {
                case 'customization':
                    return <BgColorsOutlined style={{ fontSize: 12, color: '#999' }} />;
                case 'fulfillment':
                    return <FulfillmentIcon style={{ fontSize: 10, color: '#999' }} />;
                case 'shipment':
                    return <ShipmentIcon style={{ fontSize: 10, color: '#999' }} />;
                case 'tracking':
                    return <TrackingIcon style={{ fontSize: 10, color: '#999' }} />;
                default:
                    return null;
            }
        }

        switch (type) {
            case 'customization':
                return <BgColorsOutlined style={{ fontSize: 12 }} />;
            case 'fulfillment':
                return <FulfillmentIcon style={{ fontSize: 10 }} />;
            case 'shipment':
                return <ShipmentIcon style={{ fontSize: 10 }} />;
            case 'tracking':
                return <TrackingIcon style={{ fontSize: 10 }} />;
            default:
                return null;
        }
    };

    // Helper function to get short status text with better abbreviations
    const getShortStatus = (type, value) => {
        // Check status consistency first
        const consistency = getStatusConsistency(type);
        if (!consistency.isConsistent) {
            return 'Mixed';
        }

        switch (type) {
            case 'customization':
                switch (value) {
                    case 'NoCustomization':
                        return 'N/A';
                    case 'TaskCreated':
                        return 'Task';
                    case 'Doing':
                        return 'Work';
                    case 'InternalReview':
                        return 'Review';
                    case 'Rework':
                        return 'Redo';
                    case 'MockupReady':
                        return 'Ready';
                    case 'MockupSent':
                        return 'Sent';
                    case 'Confirming':
                        return 'Wait';
                    case 'CustomerConfirmed':
                        return 'OK';
                    case 'ConfirmedNoReply':
                        return 'Auto';
                    case 'AutoFulfill':
                        return 'Auto';
                    default:
                        return '-';
                }
            case 'fulfillment':
                switch (value) {
                    case 'Unfulfilled':
                        return 'Wait';
                    case 'Fulfilled':
                        return 'Done';
                    case 'Shipped':
                        return 'Ship';
                    case 'Canceled':
                        return 'X';
                    default:
                        return '-';
                }
            case 'shipment':
                return value ? '✓' : '○';
            case 'tracking':
                if (!value || !value.lastStatus) return '-';
                switch (value.lastStatus?.toLowerCase()) {
                    case 'delivered':
                        return '✓';
                    case 'out_for_delivery':
                        return 'Out';
                    case 'in_transit':
                        return 'Move';
                    case 'pending':
                        return 'Wait';
                    case 'exception':
                    case 'failed':
                        return '!';
                    default:
                        return '?';
                }
            default:
                return '-';
        }
    };

    // Get tooltip content for each status
    const getTooltipContent = (type, value) => {
        // Check status consistency first
        const consistency = getStatusConsistency(type);
        if (!consistency.isConsistent) {
            const total = consistency.totalItems;
            const statusBreakdown = Object.entries(consistency.statusCounts)
                .map(([status, count]) => `${status}: ${count}/${total}`)
                .map(line => <span key={line}><CaretRightOutlined />{line}<br /></span>);
            
            const statusTypeLabels = {
                'customization': 'Customization Status',
                'fulfillment': 'Fulfillment Status', 
                'shipment': 'Confirm Shipment',
                'tracking': 'Shipment Status'
            };
            
            return <span>{`${statusTypeLabels[type]} - Mixed Status:`}<br></br>{statusBreakdown}</span>;
        }

        switch (type) {
            case 'customization':
                return `Customization Status: ${value || 'Not Set'}`;
            case 'fulfillment':
                return `Fulfillment Status: ${value || 'Not Set'}`;
            case 'shipment':
                return `Confirm Shipment: ${value ? 'Confirmed' : 'Pending'}`;
            case 'tracking':
                if (!value || !value.lastStatus) return 'Shipment Status: No Data';
                return `Shipment Status: ${value.lastStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
            default:
                return '';
        }
    };

    // Helper function to get order status background color (lighter tones)
    const getOrderStatusBgColor = (status) => {
        switch (status) {
            case 'Pending':
                return '#fff7e6'; // Light yellow
            case 'Shipped':
                return '#f6ffed'; // Light green
            case 'Delivered':
                return '#f6ffed'; // Light green
            case 'Canceled':
                return '#fff2f0'; // Light red
            case 'Unshipped':
                return '#f0f5ff'; // Light blue
            case 'PartiallyShipped':
                return '#f9f0ff'; // Light purple
            default:
                return '#fafafa'; // Light gray
        }
    };

    // Helper function to get order status text color (darker for contrast)
    const getOrderStatusTextColor = (status) => {
        switch (status) {
            case 'Pending':
                return '#d48806'; // Dark yellow
            case 'Shipped':
                return '#389e0d'; // Dark green
            case 'Delivered':
                return '#389e0d'; // Dark green
            case 'Canceled':
                return '#cf1322'; // Dark red
            case 'Unshipped':
                return '#1d39c4'; // Dark blue
            case 'PartiallyShipped':
                return '#531dab'; // Dark purple
            default:
                return '#595959'; // Dark gray
        }
    };

    // Helper function to get order status text (full text, not abbreviated)
    const getOrderStatusText = (status) => {
        // Return the full status text
        return status || 'Unknown';
    };

    return (
        <div className="status-overview-container">
            {/* Action Bar - vertical on the left */}
            <div 
                className="status-overview-actions"
                onClick={(e) => {
                    e.stopPropagation();
                    if (onMessageClick) {
                        onMessageClick(record.order.messages || [], record.order);
                    }
                }}
            >
                <Tooltip title={
                    hasMessages 
                        ? (unprocessedCount > 0 
                            ? `${unprocessedCount} unprocessed customer message${unprocessedCount > 1 ? 's' : ''} - Click to view`
                            : 'All customer messages processed - Click to view')
                        : 'No customer messages'
                }>
                    <Badge 
                        count={unprocessedCount} 
                        size="small"
                        style={{ 
                            backgroundColor: '#ff4d4f',
                            fontSize: '10px',
                            height: '16px',
                            lineHeight: '16px',
                            minWidth: '16px',
                            borderRadius: '8px',
                            boxShadow: unprocessedCount > 0 ? '0 2px 4px rgba(255, 77, 79, 0.3)' : 'none'
                        }}
                        offset={[4, 0]}
                    >
                        <MessageIcon style={{ 
                            fontSize: '12px',
                            color: hasMessages ? '#1890ff' : '#d1d1d1'
                        }} />
                    </Badge>
                </Tooltip>
            </div>

            {/* Main content with 4 status boxes */}
            <div className="status-overview-main">
                {/* 4 Status Overview Boxes */}
                <div className="status-overview-boxes">
                    {/* Customization Status */}
                    <Tooltip title={getTooltipContent('customization', record.customizationStatus)}>
                        <div className={`status-overview-box ${getStatusColorClass('customization', record.customizationStatus)}`}>
                            {getStatusIcon('customization')}
                        </div>
                    </Tooltip>

                    {/* Fulfillment Status */}
                    <Tooltip title={getTooltipContent('fulfillment', record.fulfillmentStatus)}>
                        <div className={`status-overview-box ${getStatusColorClass('fulfillment', record.fulfillmentStatus)}`}>
                            {getStatusIcon('fulfillment')}
                        </div>
                    </Tooltip>

                    {/* Confirm Shipment */}
                    <Tooltip title={getTooltipContent('shipment', record.isConfirmShipped)}>
                        <div className={`status-overview-box ${getStatusColorClass('shipment', record.isConfirmShipped)}`}>
                            {getStatusIcon('shipment')}
                        </div>
                    </Tooltip>

                    {/* Shipment Status */}
                    <Tooltip title={getTooltipContent('tracking', record.trackingInfo)}>
                        <div className={`status-overview-box ${getStatusColorClass('tracking', record.trackingInfo)}`}>
                            {getStatusIcon('tracking')}
                        </div>
                    </Tooltip>
                </div>

                {/* Short status text below boxes */}
                <div className="status-overview-labels">
                    <div className="status-overview-label">
                        {getShortStatus('customization', record.customizationStatus)}
                    </div>
                    <div className="status-overview-label">
                        {getShortStatus('fulfillment', record.fulfillmentStatus)}
                    </div>
                    <div className="status-overview-label">
                        {getShortStatus('shipment', record.isConfirmShipped)}
                    </div>
                    <div className="status-overview-label">
                        {getShortStatus('tracking', record.trackingInfo)}
                    </div>
                </div>
            </div>

            {/* Order Status - vertical on the right */}
            <div 
                className="status-overview-order-status"
                style={{
                    backgroundColor: getOrderStatusBgColor(orderStatus),
                    color: getOrderStatusTextColor(orderStatus)
                }}
            >
                <Tooltip title={`Order Status: ${orderStatus || 'Unknown'}`}>
                    <div className="order-status-text">
                        {getOrderStatusText(orderStatus)}
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};

export default StatusOverview;
